import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, Check, CheckCheck, Trash2, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import SupervisorNotificationPreferences from './SupervisorNotificationPreferences';
import { 
  playNotificationSound, 
  playUrgentAlertSound, 
  playAssignmentSound,
  resumeAudioContext 
} from '@/utils/notificationSound';

interface SupervisorNotification {
  id: string;
  supervisor_user_id: string;
  patient_id: string | null;
  triage_session_id: string | null;
  notification_type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationPreferences {
  sound_enabled: boolean;
  notifications_enabled: boolean;
}

export default function SupervisorNotificationBell() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<SupervisorNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const preferencesRef = useRef<NotificationPreferences | null>(null);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from('supervisor_notifications')
      .select('*')
      .eq('supervisor_user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error fetching notifications:', error);
    } else {
      setNotifications(data || []);
    }
    setLoading(false);
  };

  // Fetch user's sound preferences
  const fetchSoundPreferences = async () => {
    if (!user?.id) return;
    
    const { data } = await supabase
      .from('supervisor_notification_preferences')
      .select('sound_enabled, notifications_enabled')
      .eq('user_id', user.id)
      .maybeSingle();
    
    preferencesRef.current = data || { sound_enabled: true, notifications_enabled: true };
  };

  // Play sound based on notification type
  const playNotificationTypeSound = async (notificationType: string) => {
    // Check if sounds are enabled
    if (!preferencesRef.current?.sound_enabled) return;
    
    await resumeAudioContext();
    
    switch (notificationType) {
      case 'red_flag_alert':
        playUrgentAlertSound();
        break;
      case 'patient_assigned':
      case 'patient_unassigned':
        playAssignmentSound();
        break;
      case 'triage_completed':
      default:
        playNotificationSound();
        break;
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchSoundPreferences();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('supervisor-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'supervisor_notifications',
          filter: `supervisor_user_id=eq.${user?.id}`
        },
        async (payload) => {
          const newNotification = payload.new as SupervisorNotification;
          setNotifications(prev => [newNotification, ...prev]);
          
          // Play sound based on notification type
          await playNotificationTypeSound(newNotification.notification_type);
          
          // Show toast with appropriate styling for urgent notifications
          if (newNotification.notification_type === 'red_flag_alert') {
            toast.error(newNotification.title, {
              description: newNotification.message,
              duration: 10000, // Keep on screen longer
            });
          } else {
            toast.info(newNotification.title, {
              description: newNotification.message
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const markAsRead = async (id: string) => {
    const { error } = await supabase
      .from('supervisor_notifications')
      .update({ is_read: true })
      .eq('id', id);

    if (!error) {
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;

    const { error } = await supabase
      .from('supervisor_notifications')
      .update({ is_read: true })
      .in('id', unreadIds);

    if (!error) {
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
      toast.success('All notifications marked as read');
    }
  };

  const deleteNotification = async (id: string) => {
    const { error } = await supabase
      .from('supervisor_notifications')
      .delete()
      .eq('id', id);

    if (!error) {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }
  };

  const handleNotificationClick = async (notification: SupervisorNotification) => {
    await markAsRead(notification.id);
    
    if (notification.patient_id) {
      setOpen(false);
      navigate(`/admin/patients/${notification.patient_id}`);
    }
  };

  const getRecommendationColor = (message: string) => {
    if (message.includes('ER_NOW')) return 'text-destructive';
    if (message.includes('URGENT_CARE')) return 'text-orange-600';
    if (message.includes('PCP')) return 'text-yellow-600';
    if (message.includes('HOME')) return 'text-green-600';
    return '';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notifications</h4>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
            <SupervisorNotificationPreferences />
          </div>
        </div>
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Loading...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="flex-1 min-w-0"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-center gap-2">
                        {!notification.is_read && (
                          <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                        <p className="font-medium text-sm truncate">
                          {notification.title}
                        </p>
                      </div>
                      <p className={`text-sm mt-1 ${getRecommendationColor(notification.message)}`}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      {notification.patient_id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notification);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
